import { useState } from 'react';
import { useApp } from '../store';
import { can } from '../permissions';
import Admin from './Admin';
import MemberPeople from './MemberPeople';
import WorkspaceSettings from './WorkspaceSettings';
import { useTabKeys } from './useTabKeys';

/**
 * THE WORKSPACE — everything about the workspace in one home, instead of scattered
 * across a person's Account and a separate People screen.
 *
 * Two tabs. "People & access" is who is in the workspace and what they may do —
 * the full management screen for a manager or admin, the read-only member view for
 * everyone else (#359). "Settings" is the workspace itself — its name, and how its
 * money is reported (base currency + exchange rates), moved here off the personal
 * Account where a workspace-wide setting never belonged.
 *
 * This separates the three scopes the app was mixing: YOU (Account), THIS WORKSPACE
 * (here), and THIS BOARD (Settings).
 */
export default function WorkspaceHub() {
  const workspaceRole = useApp((s) => s.role);
  const [tab, setTab] = useState<'people' | 'settings'>('people');
  const tabKeys = useTabKeys(['people', 'settings'] as const, tab, setTab);

  return (
    // Capped to the app's page width like every other screen — the whole hub is
    // one block so the first child of .view carries the same max-width cap the
    // other screens get from .settings (the size-consistency check relies on it).
    <div className="ws-hub">
      <div className="tabs ws-hub-tabs" role="tablist" onKeyDown={tabKeys.onKeyDown}>
        <button
          role="tab"
          aria-selected={tab === 'people'}
          ref={tabKeys.setRef('people')}
          tabIndex={tabKeys.tabIndex('people')}
          className={`tab ${tab === 'people' ? 'is-on' : ''}`}
          onClick={() => setTab('people')}
        >
          People &amp; Access
        </button>
        <button
          role="tab"
          aria-selected={tab === 'settings'}
          ref={tabKeys.setRef('settings')}
          tabIndex={tabKeys.tabIndex('settings')}
          className={`tab ${tab === 'settings' ? 'is-on' : ''}`}
          onClick={() => setTab('settings')}
        >
          Settings
        </button>
      </div>

      {tab === 'people' ? (
        can(workspaceRole, 'setRoles') ? <Admin /> : <MemberPeople />
      ) : (
        <WorkspaceSettings />
      )}
    </div>
  );
}
